import { useCallback, useEffect, useState } from "react";
import {
  DEFAULT_VIEW,
  VIEW_CHANGE_EVENT,
  VIEW_ROLES,
  VIEW_STORAGE_KEY,
  isRepsityView,
  readStoredView,
  type CanonicalRole,
  type RepsityView,
} from "@/lib/views";

/**
 * `useView()` — the single source of truth for the "View as" lens.
 *
 * One localStorage key (`repsity:viewAs`) + one broadcast event, so the sidebar
 * switcher and every `<ViewGate>` stay in lockstep without a provider:
 *   • `setView` writes storage, updates local state, and dispatches a custom
 *     event so OTHER hook instances in this tab re-render immediately.
 *   • a `storage` listener syncs across tabs.
 *
 * This is a VIEW, not access control — see `@/lib/views`.
 */
export function useView(): {
  view: RepsityView;
  setView: (next: RepsityView) => void;
  roles: CanonicalRole[];
} {
  const [view, setViewState] = useState<RepsityView>(() => readStoredView());

  const setView = useCallback((next: RepsityView) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(VIEW_STORAGE_KEY, next);
      window.dispatchEvent(new CustomEvent(VIEW_CHANGE_EVENT, { detail: next }));
    }
    setViewState(next);
  }, []);

  useEffect(() => {
    // Same-tab: another hook instance (or the switcher) changed the view.
    const onChange = (e: Event) => {
      const next = (e as CustomEvent).detail;
      if (isRepsityView(next)) setViewState(next);
    };
    // Cross-tab: localStorage changed in another tab.
    const onStorage = (e: StorageEvent) => {
      if (e.key === VIEW_STORAGE_KEY && isRepsityView(e.newValue)) {
        setViewState(e.newValue);
      }
    };
    window.addEventListener(VIEW_CHANGE_EVENT, onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(VIEW_CHANGE_EVENT, onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return { view, setView, roles: VIEW_ROLES[view] ?? VIEW_ROLES[DEFAULT_VIEW] };
}
